import type { ReactElement } from 'react';
import React, { useCallback } from 'react';
import type { UseComboboxPropGetters } from 'downshift';
import type { VirtualItem } from '@tanstack/react-virtual';
import { theme } from '@balance-web/theme';

import { MenuItem, MenuItemLoadingIndicator } from './styled-components';

type UseItemMapProps<Item> = {
  getItemProps: UseComboboxPropGetters<Item>['getItemProps'];
  itemRenderer: (item: Item) => ReactElement;
  itemToValue: (item: Item) => string | number;
  itemToSelectable?: (item: Item) => boolean;
  isSticky?: (index: number) => boolean;
  isActiveSticky?: (index: number) => boolean;
  items: Item[];
  selectedItem: Item | null;
};

export function useItemMap<Item>({
  getItemProps,
  itemRenderer,
  itemToValue,
  itemToSelectable,
  items,
  selectedItem,
  isSticky,
  isActiveSticky,
}: UseItemMapProps<Item>) {
  return useCallback(
    (virtualRow: VirtualItem) => {
      const _isStick = isSticky && isSticky(virtualRow.index);

      const _isActiveSticky =
        isActiveSticky && isActiveSticky(virtualRow.index);

      const virtualisationStyles = {
        height: virtualRow.size,
        inset: 0,
        ...(_isStick
          ? {
              background: theme.palette.background.dialog,
              zIndex: 1,
            }
          : {}),

        ...(_isActiveSticky
          ? {
              position: 'sticky' as const,
            }
          : {
              position: 'absolute' as const,
              transform: `translateY(${virtualRow.start}px)`,
            }),
      };

      // paginated results render a spinner as the last item
      const isLoadingRow = virtualRow.index > items.length - 1;
      if (isLoadingRow) {
        return (
          <MenuItemLoadingIndicator
            key="loading-indicator"
            style={virtualisationStyles}
          />
        );
      }

      // transform consumer data
      const item = items[virtualRow.index];
      const itemValue = itemToValue(item);
      const isSelected = !!(
        selectedItem && itemToValue(selectedItem) === itemValue
      );

      const isItemSelectable =
        itemToSelectable === undefined ? true : itemToSelectable(item);

      const downshiftItemProps = getItemProps({
        isSelected,
        index: virtualRow.index,
        item,
        style: virtualisationStyles,

        // Do not set aria-selected manually here, it's controlled by downshift
      });

      return (
        <MenuItem
          // Prefer unique key to `virtualRow.index` — avoids issues with
          // React's reconciliation during search.
          key={itemValue}
          {...downshiftItemProps}
          isItemSelectable={isItemSelectable}
        >
          {itemRenderer(item)}
        </MenuItem>
      );
    },
    [
      isSticky,
      isActiveSticky,
      items,
      itemToValue,
      selectedItem,
      itemToSelectable,
      getItemProps,
      itemRenderer,
    ]
  );
}
